import React, { useState, useEffect, useCallback } from 'react';
import { View, FlatList, StyleSheet, TouchableOpacity, RefreshControl, StatusBar, Modal } from 'react-native';
import { Card, Text, Avatar, Button, Chip, useTheme, ActivityIndicator, Snackbar, Surface, Badge, IconButton } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { getPendingVisits, approveVisit, cancelVisit } from '../../../api/adminAPI';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { formatDateTime } from '../../../utils/dateUtils';

const PendingVisitsScreen = ({ navigation }) => {
  const theme = useTheme();
  const [pendingVisits, setPendingVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [approveDialogVisible, setApproveDialogVisible] = useState(false);
  const [approvingVisit, setApprovingVisit] = useState(false);
  const [cancelDialogVisible, setCancelDialogVisible] = useState(false);
  const [cancellingVisit, setCancellingVisit] = useState(false);

  const fetchPendingVisits = async () => {
    try {
      setLoading(true);
      const response = await getPendingVisits();
      
      if (response.success) {
        setPendingVisits(response.data);
      } else {
        setSnackbarMessage(response.message || 'Onay bekleyen ziyaretler yüklenemedi');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error fetching pending visits:', error);
      setSnackbarMessage(error.message || 'Onay bekleyen ziyaretler yüklenemedi');
      setSnackbarVisible(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPendingVisits();
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchPendingVisits();
    }, [])
  );

  const onRefresh = () => {
    setRefreshing(true);
    fetchPendingVisits();
  };

  const onApproveVisit = async () => {
    if (!selectedVisit) return;
    
    try {
      setApprovingVisit(true);
      const response = await approveVisit(selectedVisit.id);
      
      if (response.success) {
        setSnackbarMessage('Ziyaret başarıyla onaylandı');
        fetchPendingVisits();
      } else {
        setSnackbarMessage(response.message || 'Ziyaret onaylanırken bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error approving visit:', error);
      setSnackbarMessage(error.message || 'Ziyaret onaylanırken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setApprovingVisit(false);
      setApproveDialogVisible(false);
      setSelectedVisit(null);
    }
  };

  const onCancelVisit = async () => {
    if (!selectedVisit) return;
    
    try {
      setCancellingVisit(true);
      const response = await cancelVisit(selectedVisit.id);
      
      if (response.success) {
        setSnackbarMessage('Ziyaret talebi başarıyla iptal edildi');
        fetchPendingVisits();
      } else {
        setSnackbarMessage(response.message || 'Ziyaret iptal edilirken bir hata oluştu');
        setSnackbarVisible(true);
      }
    } catch (error) {
      console.error('Error cancelling visit:', error);
      setSnackbarMessage(error.message || 'Ziyaret iptal edilirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setCancellingVisit(false);
      setCancelDialogVisible(false);
      setSelectedVisit(null);
    }
  };

  const renderVisitItem = ({ item: visit }) => {
    const childFirstName = visit && visit.child ? visit.child.first_name || '' : '';
    const childLastName = visit && visit.child ? visit.child.last_name || '' : '';
    const childName = childFirstName && childLastName ? 
      `${childFirstName} ${childLastName}` : 
      childFirstName || childLastName || 'İsimsiz Çocuk';
    
    // Veli adını doğrudan parent_name alanından al
     // Veli adını doğrudan parent_name alanından al
     const parentName = visit && visit.child ? visit.child.parent_name || 'İsimsiz Ebeveyn' : 'İsimsiz Ebeveyn';
     const parentPhone = visit && visit.child ? visit.child.parent_phone || '-' : '-';
     
    const avatarText = childFirstName ? childFirstName.charAt(0).toUpperCase() : 'C';
    
    const requestedTime = visit && visit.requested_time ? formatDateTime(visit.requested_time) : '-';
    
    
    return (
      <Card style={styles.card}>
        <Card.Content style={styles.cardContent}>
          <View style={styles.headerRow}>
            <Avatar.Text size={40} label={avatarText} style={styles.avatar} />
            <View style={styles.headerText}>
              <Text style={styles.childName}>{childName}</Text>
              <Text style={styles.parentName}>Veli: {parentName}</Text>
              <Text style={styles.parentPhone}>Tel:  {parentPhone}</Text>
            </View>
            <Badge size={24} style={styles.statusBadge}>Onay Bekliyor</Badge>
          </View>
          
          <View style={styles.divider} />
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Giriş Talep Tarihi:</Text>
            <Text style={styles.detailValue}>{requestedTime}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Aktivite Tipi:</Text>
            <Text style={styles.detailValue}>
              {visit && visit.activityType ? visit.activityType.name || 'Oyun Parkı' : 'Oyun Parkı'}
            </Text>
          </View>
          
          {visit && visit.notes && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Notlar:</Text>
              <Text style={styles.detailValue}>{visit.notes}</Text>
            </View>
          )}
          
          <View style={styles.buttonsRow}>
            <Button 
              mode="contained" 
              icon="check-circle"
              onPress={() => {
                setSelectedVisit(visit);
                setApproveDialogVisible(true);
              }}
              style={styles.approveButton}
              color="#fff"
            >
              Onayla
            </Button>
            
            <Button 
              mode="outlined" 
              icon="close-circle"
              onPress={() => {
                setSelectedVisit(visit);
                setCancelDialogVisible(true);
              }}
              color="rgb(125, 76, 236)"
              style={styles.cancelButton}
            >
              İptal Et
            </Button>
          </View>
        </Card.Content>
      </Card>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="rgb(125, 76, 236)" />
        <Text style={styles.loadingText}>Onay bekleyen ziyaretler yükleniyor...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <StatusBar backgroundColor="rgb(125, 76, 236)" barStyle="light-content" />
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.navigate('VisitsHub')} style={styles.backButton}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Onay Bekleyen Ziyaretler</Text>
      </View>

      <FlatList
        data={pendingVisits}
        renderItem={renderVisitItem}
        keyExtractor={(item) => item.id?.toString()}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={[theme.colors.primary]}
          />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="clock-time-four-outline" size={80} color="rgb(125, 76, 236)" />
            <Text style={styles.emptyText}>Onay bekleyen ziyaret bulunmuyor</Text>
          </View>
        }
      />
      
      {/* Onay Modal */}
      <Modal
        visible={approveDialogVisible}
        transparent={true}
        onRequestClose={() => setApproveDialogVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <MaterialCommunityIcons 
              name="check-circle-outline" 
              size={50} 
              color="rgb(125, 76, 236)"
              style={styles.modalIcon} 
            />
            
            <Text style={styles.modalTitle}>Ziyaret Onayı</Text>
            <Text style={styles.modalText}>
              {selectedVisit?.child?.first_name} {selectedVisit?.child?.last_name} için girişi onaylamak istiyor musunuz?
            </Text>
            
            <View style={styles.modalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={onApproveVisit}
                loading={approvingVisit}
                style={[styles.modalButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
                icon="check-circle"
                color="#fff"
              >
                Onayla
              </Button>
              <Button 
                mode="outlined" 
                onPress={() => setApproveDialogVisible(false)}
                style={styles.modalButton}
                color="rgb(125, 76, 236)"
              >
                Vazgeç
              </Button>
            </View>
          </View>
        </View>
      </Modal>
      
      {/* İptal Modal */}
      <Modal
        visible={cancelDialogVisible}
        transparent={true}
        onRequestClose={() => setCancelDialogVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <MaterialCommunityIcons 
              name="close-circle-outline" 
              size={50} 
              color="rgb(125, 76, 236)"
              style={styles.modalIcon} 
            />
            
            <Text style={styles.modalTitle}>Ziyaret İptali</Text>
            <Text style={styles.modalText}>
              {selectedVisit?.child?.first_name} {selectedVisit?.child?.last_name} için ziyaret talebini iptal etmek istiyor musunuz?
            </Text>
            
            <View style={styles.modalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={onCancelVisit}
                loading={cancellingVisit}
                style={[styles.modalButton, { backgroundColor: 'rgb(125, 76, 236)' }]}
                icon="close-circle"
                color="#fff"
              >
                İptal Et
              </Button>
              <Button 
                mode="outlined" 
                onPress={() => setCancelDialogVisible(false)}
                style={styles.modalButton}
                color="rgb(125, 76, 236)"
              >
                Vazgeç
              </Button>
            </View>
          </View>
        </View>
      </Modal>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        style={{backgroundColor: 'rgb(125, 76, 236)'}}
        theme={{colors: {surface: '#fff'}}}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
          color: '#fff'
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)'
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    width: '80%',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    borderRadius: 10,
    padding: 20,
    alignItems: 'center',
    elevation: 5,
    borderColor: 'rgb(125, 76, 236)',
    borderWidth: 1,
  },
  modalIcon: {
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
  },
  modalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  modalButton: {
    marginHorizontal: 5,
    minWidth: 120,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgb(125, 76, 236)',
    paddingVertical: 16,
    paddingHorizontal: 15,
    elevation: 4,
  },
  backButton: {
    marginRight: 15,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF'
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#555'
  },
  listContent: {
    padding: 10,
    paddingBottom: 20
  },
  card: {
    marginBottom: 10,
    elevation: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.8)'
  },
  cardContent: {
    padding: 10
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10
  },
  avatar: {
    marginRight: 15,
    backgroundColor: 'rgb(125, 76, 236)'
  },
  headerText: {
    flex: 1
  },
  childName: {
    fontSize: 18,
    fontWeight: 'bold'
  },
  parentName: {
    fontSize: 14,
    color: '#616161',
  },
  parentPhone: {
    fontSize: 14,
    color: '#616161',
    marginTop: 2,
  },
  statusBadge: {
    backgroundColor: 'rgb(125, 76, 236)',
    color: 'white'
  },
  durationChip: {
    height: 30
  },
  divider: {
    height: 1,
    backgroundColor: '#E0E0E0',
    marginVertical: 10
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 8
  },
  detailLabel: {
    width: 100,
    fontWeight: 'bold',
    color: 'rgb(125, 76, 236)'
  },
  detailValue: {
    flex: 1,
    color: '#212121'
  },
  approveButton: {
    marginTop: 10,
    borderRadius: 4,
    flex: 1,
    marginRight: 8,
    backgroundColor: 'rgb(125, 76, 236)'
  },
  cancelButton: {
    marginTop: 10,
    borderRadius: 4,
    flex: 1,
    marginLeft: 8,
    borderColor: 'rgb(125, 76, 236)'
  },
  buttonsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10
  },
  emptyContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 50
  },
  emptyText: {
    marginTop: 10,
    fontSize: 16,
    color: '#757575',
    textAlign: 'center'
  }
});

export default PendingVisitsScreen;
